// ===================
// Editor pane layout
// ===================
//
// The section/question/document editors are three-pane: outline rail |
// document | AI panel. The side panes have persisted, user-dragged pixel
// widths; the document pane is the flex remainder. Nothing used to stop the
// remainder collapsing, so a wide AI panel plus the outline rail could squeeze
// the document — the workspace the user is actually writing in — down to a
// fifth of the window (a ~260px column wrapping prose at five words a line).
//
// `resolvePaneWidths` clamps the *rendered* side-pane widths so the document
// always keeps `MIN_EDITOR_WIDTH` when the viewport can afford it. The stored
// preferences are left untouched: a narrow window borrows space from the side
// panes, and a wide one gives it straight back.

export const MIN_AI_PANEL_WIDTH = 300
export const MAX_AI_PANEL_WIDTH = 700
export const DEFAULT_AI_PANEL_WIDTH = 360
export const MIN_OUTLINE_WIDTH = 200
export const MAX_OUTLINE_WIDTH = 320
export const DEFAULT_OUTLINE_WIDTH = 200

/**
 * Comfortable minimum for the document column. Roughly a 65-character measure
 * at the editor's prose width plus its gutters — below this, paragraph rhythm
 * and word-count targets stop being judgeable.
 */
export const MIN_EDITOR_WIDTH = 520

export interface PaneWidthInput {
  /** Measured width of the three-pane row. 0/unknown ⇒ no clamping. */
  containerWidth: number
  /** Stored outline width, or null when the page has no outline rail. */
  outlineWidth: number | null
  /** Stored AI panel width, or null when the panel is hidden. */
  aiPanelWidth: number | null
  /** Total px consumed by resize handles between the panes. */
  handlesWidth?: number
}

export interface ResolvedPaneWidths {
  outlineWidth: number | null
  aiPanelWidth: number | null
}

function clamp(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min
  return Math.min(max, Math.max(min, value))
}

/**
 * Clamp the side panes so the document column keeps at least
 * `MIN_EDITOR_WIDTH` px. Space is reclaimed from the AI panel first (it is the
 * widest and the most optional), then from the outline rail, each down to its
 * own minimum. If the viewport is too small for even that, the minimums win and
 * the document takes what is left — the pane is `min-w-0`, so it shrinks rather
 * than pushing the layout into a horizontal overflow.
 */
export function resolvePaneWidths({
  containerWidth,
  outlineWidth,
  aiPanelWidth,
  handlesWidth = 0,
}: PaneWidthInput): ResolvedPaneWidths {
  const outline = outlineWidth === null ? null : clamp(outlineWidth, MIN_OUTLINE_WIDTH, MAX_OUTLINE_WIDTH)
  const ai = aiPanelWidth === null ? null : clamp(aiPanelWidth, MIN_AI_PANEL_WIDTH, MAX_AI_PANEL_WIDTH)

  // Before the first measurement (and in non-DOM environments) leave the
  // stored preferences alone — clamping against a width of 0 would collapse
  // both panes to their minimums on every first paint.
  if (!Number.isFinite(containerWidth) || containerWidth <= 0) {
    return { outlineWidth: outline, aiPanelWidth: ai }
  }

  let resolvedOutline = outline ?? 0
  let resolvedAi = ai ?? 0
  let editor = containerWidth - handlesWidth - resolvedOutline - resolvedAi

  if (editor < MIN_EDITOR_WIDTH && resolvedAi > MIN_AI_PANEL_WIDTH) {
    const borrowed = Math.min(MIN_EDITOR_WIDTH - editor, resolvedAi - MIN_AI_PANEL_WIDTH)
    resolvedAi -= borrowed
    editor += borrowed
  }

  if (editor < MIN_EDITOR_WIDTH && resolvedOutline > MIN_OUTLINE_WIDTH) {
    const borrowed = Math.min(MIN_EDITOR_WIDTH - editor, resolvedOutline - MIN_OUTLINE_WIDTH)
    resolvedOutline -= borrowed
    editor += borrowed
  }

  return {
    outlineWidth: outline === null ? null : resolvedOutline,
    aiPanelWidth: ai === null ? null : resolvedAi,
  }
}

/**
 * Largest AI panel width that still leaves the document its minimum. Used to
 * clamp the live drag so the handle keeps tracking the cursor instead of
 * drifting away from a panel that has stopped growing.
 */
export function maxAiPanelWidthFor(containerWidth: number, outlineWidth: number, handlesWidth = 0): number {
  if (!Number.isFinite(containerWidth) || containerWidth <= 0) return MAX_AI_PANEL_WIDTH
  const room = containerWidth - handlesWidth - outlineWidth - MIN_EDITOR_WIDTH
  return clamp(room, MIN_AI_PANEL_WIDTH, MAX_AI_PANEL_WIDTH)
}

/**
 * Largest outline width that still leaves the document its minimum, given the
 * space the AI panel is currently taking.
 */
export function maxOutlineWidthFor(containerWidth: number, aiPanelWidth: number, handlesWidth = 0): number {
  if (!Number.isFinite(containerWidth) || containerWidth <= 0) return MAX_OUTLINE_WIDTH
  const room = containerWidth - handlesWidth - aiPanelWidth - MIN_EDITOR_WIDTH
  return clamp(room, MIN_OUTLINE_WIDTH, MAX_OUTLINE_WIDTH)
}
